import { SkeletonText, StatusBadge } from '@/components';
import { useFormatters } from '../hooks/useFormatters';
import { PermissionGuard } from '@/common/components';
import { PERMISSIONS } from '@/common/config/permissions';
import { describeVariableValue } from '../utils/dynamicContactVars';

const CAMPAIGN_STATUS = {
  completed: { tone: 'success', label: 'Completed' },
  processing: { tone: 'warning', label: 'In Progress' },
  pending: { tone: 'info', label: 'Pending' },
  scheduled: { tone: 'info', label: 'Scheduled' },
  interval: { tone: 'warning', label: 'Interval' },
  paused: { tone: 'warning', label: 'Paused' },
  stopped: { tone: 'neutral', label: 'Stopped' },
  failed: { tone: 'error', label: 'Failed' },
  cancelled: { tone: 'neutral', label: 'Cancelled' },
  canceled: { tone: 'neutral', label: 'Cancelled' },
  active: { tone: 'success', label: 'Active' },
  running: { tone: 'warning', label: 'Running' },
};

const ONCE_TYPES = new Set(['once', 'schedule_once', 'scheduled', 'schedule']);
const SEND_NOW_TYPES = new Set(['send_now', 'immediate', 'now']);

function formatDisplayDateTime(dateStr, { withAt = false } = {}) {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return null;
    const datePart = d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const timePart = d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    return withAt ? `${datePart} at ${timePart}` : `${datePart}, ${timePart}`;
  } catch {
    return null;
  }
}

function capitalizeUnit(unit, value) {
  const raw = String(unit || '')
    .toLowerCase()
    .replace(/s$/, '');
  if (!raw) return '';
  const base = raw.charAt(0).toUpperCase() + raw.slice(1);
  return Number(value) === 1 ? base : `${base}s`;
}

function formatIntervalLabel(value, unit) {
  if (value == null || value === '' || !unit) return null;
  const unitLabel = capitalizeUnit(unit, value);
  if (!unitLabel) return null;
  return `Every ${value} ${unitLabel}`;
}

function resolveScheduleType(campaign) {
  const schedule = campaign?.schedule_details || {};
  return String(
    schedule.schedule_type || campaign?.sending_mode || '',
  ).toLowerCase();
}

function formatScheduleLabel(campaign) {
  const schedule = campaign?.schedule_details || {};
  const type = resolveScheduleType(campaign);

  if (type === 'interval') {
    return (
      formatIntervalLabel(schedule.interval_value, schedule.interval_unit) ||
      'Interval'
    );
  }

  if (ONCE_TYPES.has(type)) {
    const formatted = formatDisplayDateTime(schedule.scheduled_date_time, {
      withAt: true,
    });
    return formatted ? `Scheduled for ${formatted}` : 'Scheduled';
  }

  if (SEND_NOW_TYPES.has(type) || !type) {
    if (
      schedule.interval_value != null &&
      schedule.interval_unit &&
      !schedule.scheduled_date_time
    ) {
      return formatIntervalLabel(
        schedule.interval_value,
        schedule.interval_unit,
      );
    }
    if (schedule.scheduled_date_time) {
      const formatted = formatDisplayDateTime(schedule.scheduled_date_time, {
        withAt: true,
      });
      return formatted ? `Scheduled for ${formatted}` : 'Scheduled';
    }
    return 'Send Now';
  }

  if (schedule.interval_value != null && schedule.interval_unit) {
    return formatIntervalLabel(schedule.interval_value, schedule.interval_unit);
  }
  if (schedule.scheduled_date_time) {
    const formatted = formatDisplayDateTime(schedule.scheduled_date_time, {
      withAt: true,
    });
    return formatted ? `Scheduled for ${formatted}` : 'Scheduled';
  }

  return 'Send Now';
}

function hasValue(value) {
  return value !== null && value !== undefined && value !== '';
}

function buildDetailRows(campaign) {
  const schedule = campaign?.schedule_details || {};
  const type = resolveScheduleType(campaign);
  const isInterval =
    type === 'interval' ||
    (hasValue(schedule.interval_value) && hasValue(schedule.interval_unit));
  const isOnce = ONCE_TYPES.has(type) && !isInterval;
  const isSendNow =
    SEND_NOW_TYPES.has(type) ||
    (!type && !isInterval && !hasValue(schedule.scheduled_date_time));

  const rows = [];

  let displayType = 'Send Now';
  if (isInterval) displayType = 'Interval';
  else if (isOnce) displayType = 'Once';
  else if (!isSendNow && type) {
    displayType = type
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }

  rows.push({ label: 'Schedule Type', value: displayType });

  const scheduleLabel = formatScheduleLabel(campaign);
  if (scheduleLabel) {
    rows.push({ label: 'Schedule', value: scheduleLabel });
  }

  if (isSendNow) {
    return rows;
  }

  if (isInterval) {
    const start = formatDisplayDateTime(schedule.start_date);
    if (start) rows.push({ label: 'Start', value: start });

    const end = formatDisplayDateTime(schedule.end_date);
    if (end) rows.push({ label: 'End', value: end });

    if (hasValue(schedule.max_attempts)) {
      rows.push({
        label: 'Max Attempts',
        value: String(schedule.max_attempts),
      });
    }

    if (typeof schedule.stop_on_reply === 'boolean') {
      rows.push({
        label: 'Stop on Reply',
        value: schedule.stop_on_reply ? 'Yes' : 'No',
      });
    }

    if (hasValue(schedule.remaining_attempts)) {
      rows.push({
        label: 'Remaining Attempts',
        value: String(schedule.remaining_attempts),
      });
    }
  }

  if (!isOnce && hasValue(schedule.next_execution_time)) {
    const nextDate = new Date(schedule.next_execution_time);
    if (!Number.isNaN(nextDate.getTime()) && nextDate.getTime() > Date.now()) {
      const next = formatDisplayDateTime(schedule.next_execution_time);
      if (next) rows.push({ label: 'Next Execution', value: next });
    }
  }

  return rows;
}

function ActionButton({
  onClick,
  disabled,
  pending,
  pendingLabel,
  label,
  className,
  spinnerClass,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`px-4 py-2 text-sm font-semibold rounded-xl transition-all focus:outline-none focus:ring-2 disabled:opacity-50 flex items-center justify-center gap-2 ${className}`}
    >
      {pending ? (
        <>
          <span
            className={`w-4 h-4 border-2 rounded-full animate-spin ${spinnerClass}`}
          />
          {pendingLabel}
        </>
      ) : (
        label
      )}
    </button>
  );
}

/** Compact campaign header + metrics + details. Renders flat (no outer card). */
export function CampaignSummary({
  campaign,
  isLoading,
  onBack,
  canRetry,
  onRetry,
  isRetrying,
  canStop,
  onStop,
  isStopping,
  canPause = false,
  onPause,
  isPausing = false,
  canResume = false,
  onResume,
  isResuming = false,
  canReschedule = false,
  onReschedule,
  isRescheduling = false,
}) {
  const { formatNumber } = useFormatters();
  const totalCount = Number(campaign?.total_count) || 0;
  const sentCount = Number(campaign?.sent_count) || 0;
  const failedCount = Number(campaign?.failed_count) || 0;
  const skippedCount = Number(campaign?.skipped_count) || 0;
  const pendingCount = Math.max(
    0,
    totalCount - sentCount - failedCount - skippedCount,
  );

  const statusKey = String(campaign?.status || '').toLowerCase();
  const statusMeta = CAMPAIGN_STATUS[statusKey] || {
    tone: 'neutral',
    label: campaign?.status || 'Unknown',
  };

  const displayName =
    campaign?.campaign_name ||
    campaign?.template_name ||
    (campaign?.id != null ? `Campaign #${campaign.id}` : 'Campaign');

  const detailRows = campaign ? buildDetailRows(campaign) : [];
  const variableValues = Array.isArray(campaign?.variable_values)
    ? campaign.variable_values
    : [];

  const metricsData = [
    {
      label: 'Total Recipients',
      value: totalCount,
      bgClass: 'bg-primary/5',
      borderClass: 'border-primary/20',
      textClass: 'text-primary',
    },
    {
      label: 'Sent',
      value: sentCount,
      bgClass: 'bg-emerald-500/5',
      borderClass: 'border-emerald-500/20',
      textClass: 'text-emerald-600',
    },
    {
      label: 'Pending',
      value: pendingCount,
      bgClass: 'bg-amber-500/5',
      borderClass: 'border-amber-500/20',
      textClass: 'text-amber-600',
    },
    {
      label: 'Failed',
      value: failedCount,
      bgClass: 'bg-red-500/5',
      borderClass: 'border-red-500/20',
      textClass: 'text-red-500',
    },
  ];

  const showActions =
    canReschedule || canPause || canResume || canStop || canRetry;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <button
            type="button"
            onClick={onBack}
            className="w-9 h-9 rounded-lg border border-primary text-primary hover:bg-surface-mint flex items-center justify-center transition-colors focus:outline-none shrink-0 mt-0.5"
            title="Back to Campaigns"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <div className="min-w-0 flex flex-col gap-0.5">
            {isLoading ? (
              <>
                <SkeletonText width="w-40" height="h-6" />
                <SkeletonText width="w-28" height="h-4" />
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-h6 font-bold text-text-primary truncate">
                    {displayName}
                  </h2>
                  <StatusBadge tone={statusMeta.tone}>
                    {statusMeta.label}
                  </StatusBadge>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap justify-end">
          {isLoading ? (
            <SkeletonText width="w-28" height="h-9" className="rounded-xl" />
          ) : (
            showActions && (
              <PermissionGuard permission={PERMISSIONS.WA_CAMPAIGNS.CREATE}>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  {canReschedule && (
                    <ActionButton
                      onClick={onReschedule}
                      disabled={isRescheduling}
                      pending={isRescheduling}
                      pendingLabel="Rescheduling..."
                      label="Reschedule"
                      className="bg-primary/10 hover:bg-primary/20 border border-primary/20 text-primary focus:ring-primary/40"
                      spinnerClass="border-primary/20 border-t-primary"
                    />
                  )}
                  {canPause && (
                    <ActionButton
                      onClick={onPause}
                      disabled={isPausing}
                      pending={isPausing}
                      pendingLabel="Pausing..."
                      label="Pause"
                      className="bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 text-amber-600 hover:text-amber-500 focus:ring-amber-500/40"
                      spinnerClass="border-amber-500/20 border-t-amber-600"
                    />
                  )}
                  {canResume && (
                    <ActionButton
                      onClick={onResume}
                      disabled={isResuming}
                      pending={isResuming}
                      pendingLabel="Resuming..."
                      label="Resume"
                      className="bg-primary/10 hover:bg-primary/20 border border-primary/20 text-primary focus:ring-primary/40"
                      spinnerClass="border-primary/20 border-t-primary"
                    />
                  )}
                  {canStop && (
                    <ActionButton
                      onClick={onStop}
                      disabled={isStopping}
                      pending={isStopping}
                      pendingLabel="Stopping..."
                      label="Stop"
                      className="bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-500 hover:text-red-400 focus:ring-red-500/40"
                      spinnerClass="border-red-500/20 border-t-red-500"
                    />
                  )}
                  {canRetry && (
                    <ActionButton
                      onClick={onRetry}
                      disabled={isRetrying}
                      pending={isRetrying}
                      pendingLabel="Retrying..."
                      label="Retry Failed"
                      className="bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-500 hover:text-red-400 focus:ring-red-500/40"
                      spinnerClass="border-red-500/20 border-t-red-500"
                    />
                  )}
                </div>
              </PermissionGuard>
            )
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-2">
        {metricsData.map((metric) => (
          <div
            key={metric.label}
            className={`rounded-2xl border ${metric.borderClass} ${metric.bgClass} p-4 sm:p-5 flex flex-col justify-between transition-all hover:shadow-sm`}
          >
            <p
              className={`text-sm font-semibold tracking-wide opacity-90 ${metric.textClass}`}
            >
              {metric.label}
            </p>
            {isLoading ? (
              <SkeletonText
                width="w-16"
                height="h-8"
                className="mt-2 rounded-lg"
              />
            ) : (
              <p
                className={`text-2xl sm:text-3xl font-bold text-text-primary mt-2`}
              >
                {formatNumber(metric.value)}
              </p>
            )}
          </div>
        ))}
      </div>

      {(isLoading || detailRows.length > 0) && (
        <div className="flex flex-col gap-3 pt-2">
          <h3 className="text-sm font-bold text-text-primary uppercase tracking-wider opacity-80 px-1">
            Campaign Details
          </h3>
          {isLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 p-5 rounded-2xl bg-[#F8F9FA] border border-border-default/50">
              {[...Array(6)].map((_, i) => (
                <div key={`detail-skel-${i}`} className="flex flex-col gap-1.5">
                  <SkeletonText
                    width="w-20"
                    height="h-3"
                    className="opacity-70"
                  />
                  <SkeletonText
                    width="w-32"
                    height="h-5"
                    className="rounded-md"
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="p-5 rounded-2xl bg-[#F8F9FA] border border-border-default/50">
              <dl className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-6">
                {detailRows.map((row) => (
                  <div key={row.label} className="flex flex-col gap-1 min-w-0">
                    <dt className="text-xs font-semibold text-text-secondary uppercase tracking-wide opacity-80">
                      {row.label}
                    </dt>
                    <dd className="text-sm sm:text-base font-medium text-text-primary break-words">
                      {row.value}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
      )}

      {!isLoading && variableValues.length > 0 && (
        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-bold text-text-primary uppercase tracking-wider opacity-80 px-1">
            Variables
          </h3>
          <div className="flex flex-wrap gap-2">
            {variableValues.map((value, index) => {
              const described = describeVariableValue(value);
              return (
                <span
                  key={`${index}-${described.text}`}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-border-default text-sm text-text-primary"
                >
                  <span className="font-semibold">Variable {index + 1}:</span>
                  <span className="break-all">{described.text || '—'}</span>
                  <span
                    className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${
                      described.kind === 'Dynamic'
                        ? 'bg-[#E8F5E9] text-[#1B5E20]'
                        : 'bg-[#F5F5F5] text-[#5C5C5C]'
                    }`}
                  >
                    {described.kind}
                  </span>
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
